import React, { useState, useEffect, useRef } from 'react';
import { createStakeholderShare, revokeStakeholderShare } from '../../services/projectApi';
import './StakeholderShareCell.css';

// Bir paydaşın LINK sütunu — link her zaman oluşturulmuş halde gösterilir.
// Token henüz yoksa ve projectId varsa otomatik oluşturulur.
//   - currentToken: editMeta.stakeholderAccess[key].shareToken
//   - onTokenChange(newToken): parent state'i günceller
const buildShareUrl = (token) => `${window.location.origin}/share/${token}`;

const StakeholderShareCell = ({ projectId, stakeholderKey, accessConfig, currentToken, disabled, onTokenChange }) => {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [copied, setCopied] = useState(false);
  // İki kez auto-create tetiklenmesini engelle
  const autoCreateTriedRef = useRef(false);

  // Otomatik oluşturma: projectId mevcut, token yok, daha önce denenmediyse
  // ve hücre disabled değilse (paylaşılan projede sahip olmayan kullanıcı vb.).
  useEffect(() => {
    if (currentToken) return;
    if (!projectId) return;
    if (disabled) return;
    if (autoCreateTriedRef.current) return;
    autoCreateTriedRef.current = true;
    (async () => {
      setBusy(true); setErr('');
      try {
        const share = await createStakeholderShare(projectId, stakeholderKey, accessConfig || null);
        onTokenChange?.(share.token);
      } catch (e) {
        // 404 / yetki hatası gelince bir daha deneme — kullanıcı manuel
        // refresh ikonuna basmak ister.
        setErr(e?.message || 'Link oluşturulamadı.');
      } finally { setBusy(false); }
    })();
  }, [projectId, currentToken, stakeholderKey, accessConfig, onTokenChange, disabled]);

  // currentToken sıfırlanırsa (revoke), tekrar otomatik üretmek için flag'ı resetle
  useEffect(() => { if (currentToken) autoCreateTriedRef.current = true; }, [currentToken]);

  const handleCopy = async () => {
    if (!currentToken) return;
    try {
      await navigator.clipboard.writeText(buildShareUrl(currentToken));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* ignore */ }
  };

  const handleRefresh = async () => {
    if (!projectId) return;
    setBusy(true); setErr('');
    try {
      if (currentToken) {
        await revokeStakeholderShare(projectId, stakeholderKey).catch(() => {});
      }
      autoCreateTriedRef.current = false;
      const share = await createStakeholderShare(projectId, stakeholderKey, accessConfig || null);
      autoCreateTriedRef.current = true;
      onTokenChange?.(share.token);
    } catch (e) {
      setErr(e?.message || 'Link yenilenemedi.');
    } finally { setBusy(false); }
  };

  // Henüz proje kaydedilmediği için token oluşturulamadı
  if (!projectId) {
    return (
      <div className="ssc-link-display">
        <span className="ssc-link-empty">Önce projeyi kaydet</span>
      </div>
    );
  }

  // Paylaşılan projede / sahip değilsen — link oluşturma yetkisi yok
  if (disabled && !currentToken) {
    return (
      <div className="ssc-link-display" title="Sadece proje sahibi link oluşturabilir">
        <span className="ssc-link-empty">—</span>
      </div>
    );
  }

  if (!currentToken) {
    const errClean = err && /(proje bulunamad|404)/i.test(err)
      ? 'Yetki yok / proje bulunamadı'
      : err;
    return (
      <div className="ssc-link-display" title={err}>
        <span className="ssc-link-empty">{busy ? 'Oluşturuluyor...' : (errClean || 'Link bekliyor...')}</span>
      </div>
    );
  }

  const url = buildShareUrl(currentToken);
  return (
    <div className="ssc-link-display" title={url}>
      <a className="ssc-link-text" href={url} target="_blank" rel="noopener noreferrer">{url}</a>
      <button
        type="button"
        className="ssc-icon-btn"
        onClick={handleCopy}
        title={copied ? 'Kopyalandi' : 'Linki kopyala'}
      >
        {copied ? (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
        ) : (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
        )}
      </button>
      <button
        type="button"
        className="ssc-icon-btn ssc-icon-revoke"
        onClick={handleRefresh}
        disabled={busy}
        title="Linki yenile"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
      </button>
    </div>
  );
};

export default StakeholderShareCell;
